import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (href) => {
    // If it's the pricing modal
    if (href === '#cenik') {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openPricingModal'));
        }, 500);
      } else {
        window.dispatchEvent(new CustomEvent('openPricingModal'));
      }
      return;
    }
    
    // If on subpage, go to homepage first
    if (location.pathname !== '/') {
      navigate('/' + href);
      return;
    }
    
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#1a365d] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <img loading="lazy" decoding="async"
              src="/images/logo-fizioterapija-sasa-panic-beli.avif"
              alt="Fizioterapija Saša Panič Ljubljana"
              className="h-12 w-auto mb-6"
            />
            <p className="text-white/70 mb-6 leading-relaxed">
              Strokovna fizioterapija in rehabilitacija za boljše počutje in kvaliteto življenja.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/FizioterapijaSasaPanic"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#f87171] transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/fizioterapija_sasa_panic/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#f87171] transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-6">Storitve</h4>
            <ul className="space-y-3">
              <li>
                <button onClick={() => scrollToSection('#storitve')} className="text-white/70 hover:text-[#f87171] transition-colors text-left" data-testid="footer-service-0">
                  Fizioterapevtska obravnava
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('#storitve')} className="text-white/70 hover:text-[#f87171] transition-colors text-left" data-testid="footer-service-1">
                  TECAR terapija
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('#storitve')} className="text-white/70 hover:text-[#f87171] transition-colors text-left" data-testid="footer-service-2">
                  Terapevtska masaža
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('#storitve')} className="text-white/70 hover:text-[#f87171] transition-colors text-left" data-testid="footer-service-3">
                  Suho iglanje
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-6">Informacije</h4>
            <ul className="space-y-3">
              <li>
                <button onClick={() => scrollToSection('#o-meni')} className="text-white/70 hover:text-[#f87171] transition-colors text-left" data-testid="footer-info-0">
                  O meni
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('#cenik')} className="text-white/70 hover:text-[#f87171] transition-colors text-left" data-testid="footer-info-1">
                  Cenik
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('#pricevanja')} className="text-white/70 hover:text-[#f87171] transition-colors text-left" data-testid="footer-info-2">
                  Pričevanja
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('#kontakt')} className="text-white/70 hover:text-[#f87171] transition-colors text-left" data-testid="footer-info-3">
                  Kontakt
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-6">Kontakt</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#f87171] flex-shrink-0 mt-1" />
                <a
                  href="https://maps.app.goo.gl/LMh3RZepmt6NsNaL8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-[#f87171] transition-colors"
                  data-testid="footer-address"
                >
                  Fizioterapija Saša Panič<br />
                  DoubleTree by Hilton Ljubljana<br />
                  Dunajska c. 154<br />
                  1000 Ljubljana
                </a>
              </li>
              <li>
                <a
                  href="tel:+38640464886"
                  className="flex items-center gap-3 text-white/70 hover:text-[#f87171] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 text-[#f87171]" />
                  <span>+386 40 464 886</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:fizioterapija@sasapanic.com"
                  className="flex items-center gap-3 text-white/70 hover:text-[#f87171] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 text-[#f87171]" />
                  <span>fizioterapija@sasapanic.com</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {currentYear} Fizioterapija Saša Panič. Vse pravice pridržane.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/zasebnost" className="text-white/50 hover:text-white transition-colors" data-testid="footer-privacy">
              Zasebnost
            </Link>
            <Link to="/pogoji-uporabe" className="text-white/50 hover:text-white transition-colors" data-testid="footer-terms">
              Pogoji uporabe
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
